// Self-healing controller (docs/04 §Self-healing table).
//
// Two independent safety nets:
//  1. Freeze watchdog — a setInterval checks the play loop's RAF heartbeat; if it
//     hasn't advanced for > FREEZE_MS the page is reloaded (recovers from a JS
//     exception or render stall on a weak WebView).
//  2. Nightly reload — once a day at a device-stable quiet hour the page reloads
//     to shed leaked memory AND to apply any pending Service Worker update (SW
//     updates are deliberately NOT applied mid-playback).
//
// Reload/update are injectable so tests can assert the trigger without navigating.

import { msUntilNextReload } from './reload.ts';

const FREEZE_MS = 30000;
const WATCHDOG_TICK_MS = 5000;

export interface WatchdogDeps {
  /** ms since the play loop last ticked (PlaybackEngine.msSinceFrame). */
  msSinceFrame: () => number;
  /** Device token — seeds the device-stable nightly-reload offset. */
  token: string;
  log: (code: string, message?: string) => void;
  /** Overridable for tests; defaults to location.reload. */
  reload?: () => void;
  /** Apply a waiting SW update (skipWaiting) before the nightly reload, if any. */
  applyUpdate?: () => Promise<void>;
}

export class Watchdog {
  private freezeTimer: ReturnType<typeof setInterval> | null = null;
  private nightlyTimer: ReturnType<typeof setTimeout> | null = null;
  private reload: () => void;

  constructor(private deps: WatchdogDeps) {
    this.reload = deps.reload ?? (() => location.reload());
  }

  start(): void {
    this.startFreezeWatch();
    this.scheduleNightly();
  }

  stop(): void {
    if (this.freezeTimer) clearInterval(this.freezeTimer);
    if (this.nightlyTimer) clearTimeout(this.nightlyTimer);
    this.freezeTimer = null;
    this.nightlyTimer = null;
  }

  private startFreezeWatch(): void {
    this.freezeTimer = setInterval(() => {
      if (this.deps.msSinceFrame() > FREEZE_MS) {
        this.deps.log('watchdog_freeze_reload', `frozen ${Math.round(this.deps.msSinceFrame())}ms`);
        this.reload();
      }
    }, WATCHDOG_TICK_MS);
  }

  private scheduleNightly(): void {
    const delay = msUntilNextReload(this.deps.token);
    this.nightlyTimer = setTimeout(() => void this.nightlyReload(), delay);
  }

  private async nightlyReload(): Promise<void> {
    this.deps.log('nightly_reload');
    try {
      if (this.deps.applyUpdate) await this.deps.applyUpdate();
    } catch {
      /* apply-update best effort — reload regardless */
    }
    this.reload();
  }

  /** Expose for tests: force the freeze check to run now. */
  checkFreezeNow(): void {
    if (this.deps.msSinceFrame() > FREEZE_MS) {
      this.deps.log('watchdog_freeze_reload');
      this.reload();
    }
  }
}

export { FREEZE_MS };
